import { Check, X, Zap, Star, Building2, ArrowRight, Users } from "lucide-react";

const PLANS = [
  {
    key: "basic",
    naam: "Basic",
    prijs: "59,95",
    sporters: 10,
    badge: null,
    doelgroep: "Zelfstandige personal trainers en startende coaches",
    kleur: "#6366f1",
    highlight: false,
    features_incl: [
      "Maximaal 10 sporters",
      "Dashboard & Activiteitenoverzicht",
      "Sportersbeheer",
      "Intake formulieren",
      "Schema-builder",
      "Voedingsmodule",
      "Oefenenbibliotheek",
      "Instellingen",
    ],
    features_excl: [
      "Rooster & boekingssysteem",
      "Credits & betaalpakketten",
      "Analytics",
      "Personeel & beheerders",
    ],
    cta: "Start gratis proefperiode",
  },
  {
    key: "pro",
    naam: "Pro",
    prijs: "109,95",
    sporters: 40,
    badge: "Meest gekozen",
    doelgroep: "Groeiende coaches en kleine sportscholen",
    kleur: "#C9A84C",
    highlight: true,
    features_incl: [
      "Alles uit Basic",
      "Maximaal 40 sporters",
      "Rooster & boekingssysteem",
      "Credits & betaalpakketten",
      "Mappen & templates",
      "Analytics dashboard",
      "Eigen branding & logo",
      "Geavanceerde voedingsmodule",
      "Automatische macro- & calorieberekening",
      "Schema templates",
    ],
    features_excl: [
      "Personeel & beheerders",
      "Facturatie (PDF)",
      "Community feed",
    ],
    cta: "Start nu",
  },
  {
    key: "business",
    naam: "Business",
    prijs: "168,95",
    sporters: 75,
    badge: null,
    doelgroep: "Sportscholen en organisaties met meerdere coaches",
    kleur: "#10b981",
    highlight: false,
    features_incl: [
      "Alles uit Pro",
      "Maximaal 75 sporters",
      "Personeel & beheerders",
      "Abonnementenbeheer",
      "Facturatie (PDF download)",
      "Community feed",
      "Uitgebreide analytics & rapportages",
      "Rollen- en rechtenbeheer",
      "Medewerkersaccounts",
      "Prioriteit support",
    ],
    features_excl: [],
    cta: "Start nu",
  },
];

export default function PricingSection() {
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="pricing" className="py-24 px-4 bg-[#060606] relative overflow-hidden">
      {/* Achtergrond accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,168,76,0.06) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 mb-6">
            <Building2 size={13} className="text-[#C9A84C]" />
            <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">
              Voor trainers &amp; gyms
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
            Kies jouw pakket
          </h2>
          <p className="text-zinc-500 text-base max-w-xl mx-auto leading-relaxed">
            Beheer je sporters, maak schema's en pas jouw portaal volledig aan met eigen branding.
            Transparante maandprijzen, geen verborgen kosten.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {PLANS.map((plan) => (
            <div
              key={plan.key}
              data-testid={`pricing-card-${plan.key}`}
              className={`relative rounded-2xl flex flex-col transition-all duration-300 overflow-hidden ${
                plan.highlight
                  ? "border-2 shadow-[0_0_60px_rgba(201,168,76,0.12)] scale-[1.02]"
                  : "border border-white/8 hover:border-white/15"
              }`}
              style={{
                background: plan.highlight ? "#0f0d07" : "#0d0d0d",
                borderColor: plan.highlight ? plan.kleur : undefined,
              }}
            >
              {/* Kleuraccent bovenaan */}
              <div className="h-1 w-full" style={{ background: plan.kleur }} />

              <div className="p-7 flex flex-col flex-1">
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2 translate-y-0">
                    <div className="flex items-center gap-1.5 px-4 py-1 rounded-b-xl text-black text-xs font-black uppercase tracking-wider"
                      style={{ background: plan.kleur }}>
                      <Star size={10} fill="black" />
                      {plan.badge}
                    </div>
                  </div>
                )}

                {/* Plan naam + doelgroep */}
                <div className="mb-5 mt-2">
                  <span className="text-xs font-black uppercase tracking-widest" style={{ color: plan.kleur }}>
                    {plan.naam}
                  </span>
                  <p className="text-zinc-600 text-xs mt-1 leading-snug">{plan.doelgroep}</p>
                </div>

                {/* Prijs */}
                <div className="mb-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-zinc-500 text-xl font-bold">€</span>
                    <span className="text-5xl font-black text-white tracking-tight">{plan.prijs}</span>
                    <span className="text-zinc-500 text-sm font-medium">/maand</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Users size={11} className="text-zinc-600" />
                    <p className="text-zinc-600 text-xs">
                      Maximaal {plan.sporters} sporters inbegrepen
                    </p>
                  </div>
                </div>

                {/* Features inbegrepen */}
                <ul className="space-y-2 mb-5 flex-1">
                  {plan.features_incl.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: plan.kleur + "25" }}
                      >
                        <Check size={9} style={{ color: plan.kleur }} strokeWidth={3} />
                      </div>
                      <span className="text-zinc-300 text-sm leading-relaxed">{f}</span>
                    </li>
                  ))}
                  {plan.features_excl.map((f, i) => (
                    <li key={`excl-${i}`} className="flex items-start gap-2.5 opacity-40">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-white/5">
                        <X size={9} className="text-zinc-500" strokeWidth={3} />
                      </div>
                      <span className="text-zinc-600 text-sm leading-relaxed line-through">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Extra sporters info */}
                <div className="mb-5 py-3 px-3 rounded-xl border border-white/5 bg-white/2">
                  <p className="text-zinc-600 text-xs leading-relaxed">
                    Extra sporters boven de limiet: <span className="text-zinc-400 font-semibold">€2,50/sporter/mnd</span>
                  </p>
                </div>

                {/* CTA */}
                <button
                  onClick={scrollToContact}
                  data-testid={`pricing-cta-${plan.key}`}
                  className="w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 transition-all"
                  style={plan.highlight
                    ? { background: plan.kleur, color: "#000" }
                    : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.08)" }
                  }
                >
                  {plan.cta}
                  <ArrowRight size={14} />
                </button>

                {/* Direct toegang */}
                <div className="flex items-center justify-center gap-1.5 mt-4">
                  <Zap size={11} className="text-[#C9A84C]" />
                  <span className="text-zinc-600 text-xs">Geen opstartkosten · Direct toegang</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Extra info onderaan */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          {[
            { label: "Geen opstartkosten", sub: "Direct aan de slag na activatie" },
            { label: "Maandelijks opzegbaar", sub: "Geen lange verplichtingen" },
            { label: "Extra sporters mogelijk", sub: "€2,50 per sporter extra per maand" },
          ].map(item => (
            <div key={item.label} className="bg-[#0a0a0a] border border-white/5 rounded-xl px-4 py-3">
              <p className="text-zinc-300 text-sm font-semibold">{item.label}</p>
              <p className="text-zinc-600 text-xs mt-0.5">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
